// NodeSignatureField.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

// Context
import { useBase } from 'context/BaseContext';
import { useUser } from 'context/UserContext';

// Services
import {
  SIGNATURE_SHOW_AS_TEXT,
  SIGNATURE_SHOW_AS_WRITTEN,
  getPurchaseOrderSettings,
} from 'services/bases/purchaseOrderBaseSettings';
import { resolveImageUrl } from 'services/storage/StorageAccessService';
import {
  deleteSignatureImageIfOwned,
  uploadSignatureImage,
} from 'services/storage/SignatureUploadService';

// Utilities
import {
  buildTextSignatureValue,
  buildWrittenSignatureValue,
  formatSignatureSignedAt,
  isSignatureFieldSigned,
  normalizeSignatureFieldValue,
} from 'utilities/SignatureFieldValue';

// CSS
import './NodeSignatureField.css';

// Components
import Button from 'components/button/Button';
import SignaturePad from 'components/signaturepad/SignaturePad';
import TextInput from 'components/textinput/TextInput';

function resolveUserDisplayName(user) {
  if (!user || typeof user !== 'object') return '';
  return String(
    user.title
    || user.displayName
    || user.name
    || user.email
    || '',
  ).trim();
}

function normalizeShowAs(value) {
  return String(value || '').trim().toLowerCase() === SIGNATURE_SHOW_AS_WRITTEN
    ? SIGNATURE_SHOW_AS_WRITTEN
    : SIGNATURE_SHOW_AS_TEXT;
}

/**
 * NodeSignatureField
 *
 * Schema signature field. Text or Written (pad) based on base PO settings / showAs prop.
 */
const NodeSignatureField = React.memo(({
  node = null,
  field,
  fieldLabel,
  value,
  onChange,
  readOnly = false,
  previewMode = false,
  showAs: showAsProp = null,
  /**
   * Prefill for the signer name input.
   * Omit to use the current user; pass "" to leave blank (e.g. vendor slot).
   */
  defaultName: defaultNameProp = undefined,
  /**
   * Optional override for written-mode upload. When set, Sign calls this instead
   * of the member Storage upload helper (used by the vendor portal).
   * @type {null|((args: { name: string, dataUrl: string }) => Promise<object|null|void>)}
   */
  onSignWritten = null,
  /**
   * Optional pre-resolved image URL (portal signed URLs).
   */
  resolvedImageUrl = null,
}) => {
  const { activeBase } = useBase();
  const { user } = useUser();
  const padRef = useRef(null);

  const isReadOnly = Boolean(readOnly || previewMode);
  const normalized = useMemo(() => normalizeSignatureFieldValue(value), [value]);
  const signed = isSignatureFieldSigned(normalized);
  const defaultName = defaultNameProp !== undefined && defaultNameProp !== null
    ? String(defaultNameProp).trim()
    : resolveUserDisplayName(user);

  const settingsShowAs = useMemo(
    () => getPurchaseOrderSettings(activeBase).signatureShowAs,
    [activeBase],
  );
  const showAs = normalizeShowAs(showAsProp ?? settingsShowAs);
  const isWrittenMode = showAs === SIGNATURE_SHOW_AS_WRITTEN;

  const [draftName, setDraftName] = useState(() => (
    normalized?.name || defaultName || ''
  ));
  const [padReady, setPadReady] = useState(false);
  const [signing, setSigning] = useState(false);
  const [imageSrc, setImageSrc] = useState('');

  useEffect(() => {
    if (signed) {
      setDraftName(normalized?.name || '');
      return;
    }
    setDraftName((prev) => (prev ? prev : defaultName));
  }, [defaultName, normalized?.name, signed, value]);

  useEffect(() => {
    let cancelled = false;
    const explicitUrl = String(resolvedImageUrl || '').trim();
    if (explicitUrl) {
      setImageSrc(explicitUrl);
      return undefined;
    }

    const path = String(normalized?.imageStoragePath || '').trim();
    if (!path) {
      setImageSrc('');
      return undefined;
    }

    (async () => {
      try {
        const url = await resolveImageUrl({
          nodeKey: node?.key || null,
          baseKey: activeBase?.key || node?.baseKey || null,
          storagePath: path,
        });
        if (!cancelled) setImageSrc(url || '');
      } catch {
        if (!cancelled) setImageSrc('');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    activeBase?.key,
    node?.baseKey,
    node?.key,
    normalized?.imageStoragePath,
    resolvedImageUrl,
  ]);

  const placeholder = useMemo(() => {
    const label = String(fieldLabel || field?.label || 'signature').trim() || 'signature';
    return `Type name for ${label.toLowerCase()}`;
  }, [field?.label, fieldLabel]);

  const handleDraftChange = useCallback((event) => {
    if (isReadOnly || signing) return;
    setDraftName(event?.target?.value ?? '');
  }, [isReadOnly, signing]);

  const handlePadStrokeEnd = useCallback(() => {
    setPadReady(!(padRef.current?.isEmpty?.() ?? true));
  }, []);

  const handleClearPad = useCallback(() => {
    padRef.current?.clear?.();
    setPadReady(false);
  }, []);

  const handleSignText = useCallback(() => {
    if (isReadOnly || typeof onChange !== 'function' || signing) return;
    const next = buildTextSignatureValue({
      name: draftName,
      signedByKey: user?.key || null,
    });
    if (!next) return;
    onChange(next);
  }, [draftName, isReadOnly, onChange, signing, user?.key]);

  const handleSignWritten = useCallback(async () => {
    if (isReadOnly || typeof onChange !== 'function' || signing) return;

    const name = String(draftName || '').trim();
    if (!name) {
      toast.error('Enter a name for this signature.', { autoClose: 3000 });
      return;
    }

    const available = padRef.current?.isAvailable?.();
    if (!available) {
      // Canvas unavailable — fall back to text.
      handleSignText();
      return;
    }

    if (padRef.current?.isEmpty?.()) {
      toast.error('Draw a signature first.', { autoClose: 3000 });
      return;
    }

    const dataUrl = padRef.current?.toDataURL?.('image/png');
    if (!dataUrl) {
      toast.error('Could not capture the signature.', { autoClose: 3000 });
      return;
    }

    if (typeof onSignWritten === 'function') {
      setSigning(true);
      try {
        const next = await onSignWritten({ name, dataUrl });
        if (next) onChange(next);
        setPadReady(false);
      } catch (error) {
        toast.error(error?.message || 'Could not save the signature.', {
          autoClose: 4000,
        });
      } finally {
        setSigning(false);
      }
      return;
    }

    const nodeKey = String(node?.key || '').trim();
    const baseKey = String(activeBase?.key || node?.baseKey || '').trim();
    if (!nodeKey || !baseKey) {
      toast.error('Save the record before adding a written signature.', { autoClose: 4000 });
      return;
    }

    setSigning(true);
    try {
      const storagePath = await uploadSignatureImage({
        baseKey,
        nodeKey,
        dataUrl,
        previousStoragePath: normalized?.imageStoragePath || null,
        fieldName: field?.name || null,
      });
      const next = buildWrittenSignatureValue({
        name,
        imageStoragePath: storagePath,
        signedByKey: user?.key || null,
      });
      if (!next) return;
      onChange(next);
      setPadReady(false);
    } catch (error) {
      toast.error(error?.message || 'Could not upload the signature.', {
        autoClose: 4000,
      });
    } finally {
      setSigning(false);
    }
  }, [
    activeBase?.key,
    draftName,
    field?.name,
    handleSignText,
    isReadOnly,
    node?.baseKey,
    node?.key,
    normalized?.imageStoragePath,
    onChange,
    onSignWritten,
    signing,
    user?.key,
  ]);

  const handleClear = useCallback(async () => {
    if (isReadOnly || typeof onChange !== 'function' || signing) return;
    const previousPath = normalized?.imageStoragePath || null;
    const nodeKey = String(node?.key || '').trim();
    onChange(null);
    setDraftName(defaultName || '');
    setPadReady(false);
    padRef.current?.clear?.();
    if (previousPath && nodeKey) {
      deleteSignatureImageIfOwned(previousPath, nodeKey).catch(() => {});
    }
  }, [defaultName, isReadOnly, node?.key, normalized?.imageStoragePath, onChange, signing]);

  const signedAtLabel = formatSignatureSignedAt(normalized?.signedAt);
  const canSignWritten = Boolean(String(draftName || '').trim()) && padReady && !signing;
  const canSignText = Boolean(String(draftName || '').trim()) && !signing;

  if (signed) {
    return (
      <div className={`node-signature-field${isReadOnly ? ' is-readonly' : ''}`}>
        <div className="node-signature-field-mark">
          {imageSrc ? (
            <img
              className="node-signature-field-image"
              src={imageSrc}
              alt=""
              draggable={false}
            />
          ) : (
            <div className="node-signature-field-name">
              {normalized?.name || 'Signed'}
            </div>
          )}
          <div className="node-signature-field-line" aria-hidden="true" />
          {(normalized?.name && imageSrc) || signedAtLabel ? (
            <div className="node-signature-field-meta">
              {[normalized?.name && imageSrc ? normalized.name : null, signedAtLabel]
                .filter(Boolean)
                .join(' · ')}
            </div>
          ) : null}
        </div>
        {!isReadOnly ? (
          <div className="node-signature-field-actions">
            <Button
              label="Clear"
              height="30px"
              backgroundColor="transparent"
              enabled={!signing}
              onClick={handleClear}
            />
          </div>
        ) : null}
      </div>
    );
  }

  if (isReadOnly) {
    return (
      <div className="node-signature-field is-readonly">
        <div className="node-signature-field-empty">
          {String(fieldLabel || field?.label || 'Signature').trim() || 'Signature'}
        </div>
        <div className="node-signature-field-line is-placeholder" aria-hidden="true" />
      </div>
    );
  }

  if (isWrittenMode) {
    return (
      <div className="node-signature-field">
        <TextInput
          value={draftName}
          placeholder={placeholder}
          disabled={signing}
          onChange={handleDraftChange}
        />
        <SignaturePad
          ref={padRef}
          disabled={signing}
          onStrokeEnd={handlePadStrokeEnd}
        />
        <div className="node-signature-field-actions">
          <Button
            label="Clear"
            height="30px"
            backgroundColor="transparent"
            enabled={!signing}
            onClick={handleClearPad}
          />
          <Button
            label={signing ? 'Signing…' : 'Sign'}
            height="36px"
            enabled={canSignWritten}
            onClick={handleSignWritten}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="node-signature-field">
      <div className="node-signature-field-compose">
        <TextInput
          value={draftName}
          placeholder={placeholder}
          disabled={signing}
          onChange={handleDraftChange}
        />
        <Button
          label="Sign"
          height="36px"
          enabled={canSignText}
          onClick={handleSignText}
        />
      </div>
      <div className="node-signature-field-line is-placeholder" aria-hidden="true" />
    </div>
  );
});

NodeSignatureField.displayName = 'NodeSignatureField';

export default NodeSignatureField;
